<template>
  <div
    class="location-selector"
    :class="{ 'is-focus': isFocus, 'is-show': !showlocation }"
    v-click-outside="onClickOutside"
  >
    <y-text
      v-if="showLabel"
      class="title"
      font="var(--y-regular-font)"
      color="#949494"
      size="14px"
    >
      {{ t("shared.hotel.search.location") }}
    </y-text>
    <el-autocomplete
      :disabled="locationDisabled"
      v-model="locationLabel"
      :fetch-suggestions="querySearch"
      clearable
      hide-loading
      class="location-input"
      valueKey="showLabel"
      :placeholder="t('shared.hotel.search.locationPlaceholder')"
      @select="handleSelect"
      @blur="handleBlur"
      @focus="handleFocus"
      @input="handleInput"
      ref="autocompleteRef"
    >
      <template #prefix>
        <font-icon class="search-icon" icon="icon-Location" />
      </template>
      <template #default="{ item }">
        <div class="item flex">
          <y-icon :name="iconMaps[item.locationType]" />
          <div class="item-label ml-2">
            {{ item.showLabel }}
            <div
              class="hotel-name-en"
              v-if="item.showHotelEnName && item.nameEn"
            >
              {{ item.nameEn }}
            </div>
          </div>
        </div>
      </template>
    </el-autocomplete>

    <div class="popover-trigger" ref="popoverTriggerRef" />
    <el-popover
      v-if="popoverToggle"
      ref="popoverRef"
      popper-class="location-popover"
      :virtual-ref="popoverTriggerRef"
      :visible="isPopoverVisible"
      placement="bottom-start"
      virtual-triggering
      :arrow-offset="32"
      width="504"
      :teleported="false"
    >
      <LocationPopoverContent @select="handlePopoverSelect" />
    </el-popover>
  </div>
</template>

<script setup>
import common from "@charmdeer/common";
import { ClickOutside as vClickOutside } from "element-plus";
import _ from "lodash";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  ref,
  watch
} from "vue";
import { useI18n } from "vue-i18n";

import { searchUniversalViaPost } from "@/api/biz/common";
import { getRestCity, getRestTpHotel } from "@/api/rest/common";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useToggles } from "@/hooks/useToggles";
import { useUserStoreHook } from "@/store/modules/user";

import LocationPopoverContent from "./LocationPopoverContent/index.vue";

const { t } = useI18n();
const userStore = useUserStoreHook();
const YHotelStore = useHotelStoreHook();
const { historyList } = storeToRefs(YHotelStore);
const Y_WEB_SEARCH_LOCATION_POPOVER = useToggles(
  "Y_WEB_SEARCH_LOCATION_POPOVER"
);
const Y_WEB_SEARCH_HISTORY = useToggles("Y_WEB_SEARCH_HISTORY");
const props = defineProps({
  modelValue: { type: Object, default: () => null },
  showLabel: { type: Boolean, default: true },
  locationDisabled: { type: Boolean, default: false },
  showlocation: { type: Boolean, default: true }
});
const emit = defineEmits(["update:modelValue"]);

const locationLabel = ref("");
const isFocus = ref(false);
const isPopoverVisible = ref(false);
const autocompleteRef = ref(null);
const popoverTriggerRef = ref(null);
const popoverRef = ref(null);
const showHotelEnName = computed(() => userStore.language !== "en");
const filterHistoryList = computed(() => {
  return historyList.value.filter(item => {
    const collectionName =
      item?.info?.collectionName || item?.target?.collectionName || "";
    return collectionName !== "googleMapPlace";
  });
});
const popoverToggle = computed(() => {
  const showHistoryPop =
    filterHistoryList.value.length > 0 && Y_WEB_SEARCH_HISTORY.value;
  return Y_WEB_SEARCH_LOCATION_POPOVER.value || showHistoryPop;
});
const iconMaps = {
  googleMapPolitical: "icon-location",
  city: "icon-location",
  moduleTpHotel: "icon-hotel",
  search: "icon-search"
};

watch(
  () => props.modelValue,
  newLocation => {
    locationLabel.value = newLocation?.showLabel || "";
  },
  { immediate: true, deep: true }
);

const handleSelect = item => {
  emit("update:modelValue", item);
};

const handlePopoverSelect = async rawItem => {
  let standardLocation = null;

  try {
    if (rawItem.type === "city") {
      const cityData = rawItem.data;
      standardLocation = {
        ...cityData,
        locationType: "city",
        showLabel: common.city.toString.name(cityData, userStore.language)
      };
    } else if (rawItem.type === "history") {
      const historyData = rawItem.data;

      if (historyData.target) {
        const { ref, collectionName } = historyData.target;
        if (collectionName === "hotel" || collectionName === "moduleTpHotel") {
          const hotelData = await getRestTpHotel(ref);
          standardLocation = {
            ...hotelData,
            locationType: "moduleTpHotel",
            showLabel: common.moduleTpHotel.toString.name(
              hotelData,
              userStore.language
            ),
            showHotelEnName: showHotelEnName.value
          };
        } else if (collectionName === "city") {
          const cityData = await getRestCity(ref);
          standardLocation = {
            ...cityData,
            locationType: "city",
            showLabel: common.city.toString.name(cityData, userStore.language)
          };
        }
      } else if (historyData.info) {
        const { collectionName, place_id, description } = historyData.info;
        if (collectionName === "google") {
          standardLocation = {
            _id: place_id,
            showLabel: description,
            locationType: "googleMapPolitical"
          };
        } else if (collectionName === "keyword") {
          standardLocation = {
            _id: historyData.text,
            showLabel: historyData.text,
            locationType: "search"
          };
        }
      } else if (historyData.text) {
        standardLocation = {
          _id: historyData.text,
          showLabel: historyData.text,
          locationType: "search"
        };
      }
    }
  } catch (error) {
    const keyword = rawItem.data?.text || rawItem.data?.showLabel || "";
    if (keyword) {
      standardLocation = {
        _id: keyword,
        showLabel: keyword,
        locationType: "search"
      };
    }
  }

  if (standardLocation) {
    emit("update:modelValue", standardLocation);
    closePopover();
    nextTick(() => {
      autocompleteRef.value?.blur();
    });
  }
};

const handleBlur = () => {
  isFocus.value = false;
  if (
    locationLabel.value &&
    locationLabel.value !== props.modelValue?.showLabel
  ) {
    const searchItem = {
      locationType: "search",
      showLabel: locationLabel.value,
      _id: locationLabel.value
    };
    emit("update:modelValue", searchItem);
  } else if (!locationLabel.value && props.modelValue !== null) {
    emit("update:modelValue", null);
  }
};

const querySearch = async (queryString, cb) => {
  if (!queryString) {
    cb([]);
    return;
  }
  const result = [];
  const res = await searchUniversalViaPost(queryString);
  const { city = [], moduleTpHotel = [], googleMapPolitical = [] } = res;

  city.forEach(item => {
    item.locationType = "city";
    item.showLabel = common.city.toString.name(item, userStore.language);
    item.nameEn = common.city.toString.country(item, userStore.language);
    item.showHotelEnName = true;
  });
  result.push(...city);

  const googleMapPoliticalResult = [];
  if (googleMapPolitical.length) {
    googleMapPolitical.forEach(item => {
      const hasCity = city.some(
        cityItem => cityItem.showLabel === item.description
      );
      if (!hasCity) {
        item.locationType = "googleMapPolitical";
        item.showLabel = item.description;
        item._id = item.place_id;
        googleMapPoliticalResult.push(item);
      }
    });
    result.push(...googleMapPoliticalResult);
  }

  const searchItem = {
    locationType: "search",
    showLabel: queryString,
    _id: queryString
  };

  moduleTpHotel.forEach(item => {
    item.locationType = "moduleTpHotel";
    item.showLabel = item.name;
    item.showHotelEnName = showHotelEnName.value;
  });
  result.push(...moduleTpHotel);
  result.push(searchItem);

  cb(result);
};

const openPopover = () => {
  YHotelStore.getSearchHistory();
  isPopoverVisible.value = true;
};

const closePopover = () => {
  isPopoverVisible.value = false;
};

const handleInput = value => {
  if (value) {
    closePopover();
  } else {
    openPopover();
  }
};

const handleFocus = () => {
  isFocus.value = true;
  if (!locationLabel.value) {
    openPopover();
  }
};

const onClickOutside = event => {
  const popoverEl = popoverRef.value?.popperRef?.contentRef;
  if (autocompleteRef.value?.input?.contains(event.target)) {
    return;
  }
  if (popoverEl && !popoverEl.contains(event.target)) {
    closePopover();
  } else if (!popoverEl) {
    closePopover();
  }
};

onActivated(() => {
  YHotelStore.getSearchHistory();
});

onDeactivated(() => {
  closePopover();
});

onBeforeUnmount(() => {
  closePopover();
});
</script>

<style lang="scss" scoped>
::v-global(.location-popover) {
  --el-popover-padding: 0 !important;
}
.location-selector {
  --input-direction: rtl;
  width: 188px;
  position: relative;
  .search-icon {
    font-size: 18px;
    color: #000000;
  }

  .title {
    margin-bottom: 6px;
    line-height: 1;
    display: block;
  }
  :deep(.el-input.is-disabled .el-input__wrapper) {
    box-shadow: none;
  }
  :deep(.el-input__wrapper) {
    box-shadow: none;
    padding-left: 0;
    padding-right: 0;

    .el-input__inner {
      color: var(--y-text-color);
      font-size: 18px;
      line-height: 20px;
      font-family: var(--y-medium-font);
      direction: var(--input-direction);
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.is-focus {
    --input-direction: ltr;
  }
}
.is-show {
  width: 0;
  height: 0;
  opacity: 0;
}
.item {
  line-height: 1;
  padding: 10px 0;
  .y-icon {
    margin-top: 1px;
  }
  .item-label {
    line-height: 16px;
  }
  .hotel-name-en {
    font-weight: 400;
    font-size: 14px;
    color: #949494;
    margin-top: 8px;
    line-height: 16px;
  }
}
.popover-trigger {
  position: absolute;
  width: 1px;
  height: 100%;
  left: -32px;
  top: 0;
  bottom: 0;
  z-index: -1;
  pointer-events: none;
}
</style>
